import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { LowValuesDeclarantDataSource } from '@e-origin/shared';

// Flattened Schemas

export const GetUsersSchema = z.object({
  criteria: z.any(),
});

export type GetUsers = z.infer<typeof GetUsersSchema>;

export const GetUsersResultSchema = z.object({
  data: z.object({
    users: z.array(z.any()),
    totalItems: z.number(),
  }),
});

export type GetUsersResult = z.infer<typeof GetUsersResultSchema>;

export const DeleteUserSchema = z.object({
  email: z.string().email(),
});

export type DeleteUser = z.infer<typeof DeleteUserSchema>;

export const DeleteUserResultSchema = z.undefined();

export const CreateUserSchema = z.object({
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  password: z.string(),
  role: z.string(),
  declarant: z.string(),
  isSupervisor: z.boolean().optional(),
  lowValuesDeclarantDataSource: z.nativeEnum(LowValuesDeclarantDataSource).default(LowValuesDeclarantDataSource.DECLARANT),
});

export const CreateUserResultSchema = z.object({
  data: z.any(),
  message: z.string(),
});

export const UpdateUserSchema = z.object({
  // Params merged
  userId: z.string(),
  // Body properties merged
  firstName: z.string(),
  lastName: z.string(),
  visible: z.boolean().optional(),
  deleted: z.boolean().optional(),
  role: z.string(),
  declarant: z.string(),
  isSupervisor: z.boolean().optional(),
});

export const UpdateUserResultSchema = z.object({
  data: z.any(),
  message: z.string(),
});

export const ChangeUserPasswordSchema = z.object({
  userId: z.string(),
  password: z.string(),
});

export const ChangeUserPasswordResultSchema = z.undefined();

export const ImpersonateUserSchema = z.object({
  email: z.string().email(),
});

export const ImpersonateUserResultSchema = z.object({
  token: z.string(),
});

const c = initContract();

export const usersContract = c.router({
  getUsers: {
    method: 'GET',
    path: '/admin/users',
    query: GetUsersSchema,
    responses: { 200: GetUsersResultSchema },
  },
  createUser: {
    method: 'POST',
    path: '/admin/create-user',
    body: CreateUserSchema,
    responses: { 200: CreateUserResultSchema },
  },
  editUser: {
    method: 'PATCH',
    path: '/admin/edit-user/:userId',
    pathParams: z.object({ userId: z.string() }),
    body: z.object({
      firstName: z.string(),
      lastName: z.string(),
      visible: z.boolean().optional(),
      deleted: z.boolean().optional(),
      role: z.string(),
      declarant: z.string(),
      isSupervisor: z.boolean().optional(),
    }),
    responses: { 200: UpdateUserResultSchema },
  },
  deleteUser: {
    method: 'DELETE',
    path: '/admin/users/:email',
    pathParams: z.object({ email: z.string().email() }),
    body: c.noBody(),
    responses: { 200: z.undefined() },
  },
  changePassword: {
    method: 'POST',
    path: '/admin/change-user-password/:userId',
    pathParams: z.object({ userId: z.string() }),
    body: z.object({ password: z.string() }),
    responses: { 200: z.undefined() },
  },
  impersonateUser: {
    method: 'POST',
    path: '/admin/impersonate-user',
    body: ImpersonateUserSchema,
    responses: { 200: ImpersonateUserResultSchema },
  },
});
